/* eslint-disable */
/**
 * Bundled globe client entry.
 *
 * Originally extracted from the inline <script> blocks of
 * src/views/world-map-globe.ts (globe de-string plan, Phase 1): a deferred ES
 * module plus two classic scripts that shared a global scope and communicated
 * via window.*. Those scopes survive here as two IIFEs, but their bodies are now
 * thin wiring — each concern (export PNG, filter accordion, data-type toggle,
 * view toggle, table filter, drawer, modals, settings) lives in its own ui/
 * module and is composed here. The only cross-module seams are the filter
 * accordion's collapse helper and a lazy table-filter getter (the table filter
 * is created after the data-type toggle is wired).
 */
import { interpolate as flubberInterpolate } from 'flubber'

import type { GlobeBootstrap } from '../../views/globe-bootstrap-types'
import { GlobeViewer } from './core/globe-viewer'
import { createMapboxOverlay } from './overlay/mapbox'
import { initDataTypeToggle, getStoredDataType } from './ui/data-type-toggle'
import { initDrawer } from './ui/drawer'
import { initEudiOverlay } from './ui/eudi-overlay'
import { initExportPng } from './ui/export-png'
import { initFilterAccordion } from './ui/filter-accordion'
import { initModals } from './ui/modals'
import { initGlobeSettings } from './ui/globe-settings'
import { initScramble, scrambleText } from './ui/scramble'
import { initSettingsDrawer } from './ui/settings'
import { initTableFilter, type TableFilterApi } from './ui/table-filter'
import { initViewToggle } from './ui/view-toggle'

const bootEl = document.getElementById('globe-bootstrap')
const BOOT: GlobeBootstrap = bootEl && bootEl.textContent ? JSON.parse(bootEl.textContent) : {}

window.flubberInterpolate = flubberInterpolate

// Kick off the globe geometry fetches at module load so the network overlaps
// the scramble intro instead of idling until the build timer fires. The build
// (loadGlobeData) awaits this promise rather than fetching itself.
window.__globeDataPromise = Promise.all([
  fetch('/data/dots-330.json').then((r) => r.json()),
  fetch('/data/region-render.json').then((r) => r.json()),
])
  .then(([dotsData, regionRender]) => ({
    dotsData,
    regionRender,
  }))
  // Swallow here so an early rejection doesn't surface as an unhandledrejection;
  // loadGlobeData re-fetches and reports errors if the promise resolves empty.
  .catch(() => undefined as never)

// Diegetic loading status: scramble the real build milestones into the overlay.
// The baked render bundle makes the build steps finish near-instantly, so the
// milestones are paced by a minimum-dwell sequencer rather than raw completion:
// each label holds for at least STATUS_MIN_DWELL_MS before the next can replace
// it. That floor is the per-label slice of a ~2.4s fixed loading sequence across
// the four build milestones; a genuinely slow build still advances naturally,
// since the dwell is a floor and not a fixed schedule.
{
  const statusEl = document.getElementById('globe-loading-status')
  const STATUS_MIN_DWELL_MS = 600
  const queue: string[] = []
  let advancing = false
  let settleCallbacks: Array<() => void> = []

  function settleIfIdle(): void {
    if (advancing || queue.length > 0) return
    const callbacks = settleCallbacks
    settleCallbacks = []
    callbacks.forEach((cb) => cb())
  }

  function advance(): void {
    const next = queue.shift()
    if (next === undefined) {
      advancing = false
      settleIfIdle()
      return
    }
    advancing = true
    if (statusEl) {
      scrambleText(statusEl, next, { duration: 350, startSpacing: 0.18, endSpacing: 0.12 })
    }
    window.setTimeout(advance, STATUS_MIN_DWELL_MS)
  }

  window.__setGlobeLoadingStatus = (text: string): void => {
    queue.push(text)
    if (!advancing) advance()
  }

  // Resolves once every queued milestone has held its minimum dwell. The globe
  // reveal uses this to keep the final label up while the entrance plays
  // underneath, instead of yanking the overlay the instant the build finishes.
  window.__whenGlobeLoadingStatusSettled = (cb: () => void): void => {
    settleCallbacks.push(cb)
    settleIfIdle()
  }
}

// Safety net: the globe reveal adds `globe-intro-done` to reveal the chrome in
// concert with the globe. If the globe never initializes (e.g. starting in
// table view with a zero-size container), still reveal the chrome so it can't
// get stuck hidden behind the intro start state.
window.setTimeout(() => {
  document.body.classList.add('globe-intro-done')
}, 4000)

// === module scope (originally <script type="module">) ===
;(function moduleScope() {


    // Initialize globe after text scramble animation completes (800ms)
    // This ensures the animation runs smoothly without being blocked by heavy globe work
    const container = document.getElementById('globe-container');
    let globe = null;
    if (container) {
      setTimeout(function() {
        // Don't create duplicate globes - check if one already exists
        if (window.globe) {
          return;
        }
        
        // Don't create globe with 0 dimensions - defer until container is visible
        if (container.clientWidth < 10 || container.clientHeight < 10) {
          // Mark that we need to create globe when switching to globe view
          window._globePendingInit = true;
          return;
        }
        
        globe = new GlobeViewer(container);
        window.globe = globe; // Expose globally for drawer functions
      }, 800);
    }

    initGlobeSettings();

})();

// === classic scope (originally the two non-module <script> blocks) ===
;(function classicScope() {

    // Region data for combobox typeahead
    var regionData = BOOT.regionData;
    var subnationalSearchData = BOOT.subnationalSearchData;
    // Table data for Age Assurance and Digital IDs (rebuild table on data-type change)
    window.__AGE_ASSURANCE_TABLE_DATA__ = BOOT.ageAssuranceTableData;
    window.__DIGITAL_IDS_TABLE_DATA__ = BOOT.digitalIdsTableData;
    var __MAPBOX_ACCESS_TOKEN__ = BOOT.mapboxAccessToken;

    const mapboxOverlay = createMapboxOverlay({ accessToken: __MAPBOX_ACCESS_TOKEN__, regionData });

    initScramble();
    initSettingsDrawer();
    initExportPng();

    const filterAccordion = initFilterAccordion();

    // Cmd+K (Mac) / Ctrl+K (Windows/Linux) focuses the jurisdiction search
    document.addEventListener('keydown', function(e) {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        var searchInput = document.getElementById('search-input') as HTMLInputElement | null;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    });

    // The table filter is created below, after the data-type toggle is wired.
    // The toggle reaches it lazily through this getter so its initial setTab
    // dispatch sees no table filter yet (matching the original load order), while
    // later user switches resolve the live instance.
    let tableFilter: TableFilterApi | null = null;
    initDataTypeToggle({
      eudiOverlay: initEudiOverlay(),
      getTableFilter: function() { return tableFilter; },
    });

    initViewToggle({ collapseFilterContentIfExpanded: filterAccordion.collapseIfExpanded });

    tableFilter = initTableFilter({
      regionData: regionData,
      subnationalSearchData: subnationalSearchData,
      collapseFilterContentIfExpanded: filterAccordion.collapseIfExpanded,
    });

    // The server renders the age-assurance table by default; if the persisted
    // tab is Digital IDs or EUDI Wallets, rebuild the table (header + body) now
    // that the table filter is initialized so columns match the active mode on
    // load (and, on the EUDI Wallets tab, the EUDI row filter applies).
    if (getStoredDataType() === 'digital_ids') {
      tableFilter.renderTableBody('digital_ids');
    }

    initDrawer({ mapboxOverlay });

    initModals();

})();
